/**
 * PluginDevPreviewPage — Load a plugin from a local dev server inside an iframe
 * with a real PluginBridgeHost connected. All SDK hooks work against the live
 * platform (real auth, real storage, real entity invocation).
 *
 * URL: /a/arche_plugins/dev-preview?url=http://localhost:3100&permissions=theme:read,storage:sandbox
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { RefreshCw, AlertCircle, ExternalLink, Shield, Terminal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/auth/hooks/useAuth';
import { authApi } from '@/lib/auth-api';
import { toast } from '@/components/ui/use-toast';
import { PluginBridgeHost } from '../../../components/PluginBridge';

const API_BASE = import.meta.env.VITE_API_URL || '/v1';

type PluginPermission = 'user:profile:read' | 'entities:invoke' | 'storage:sandbox' | 'theme:read';

const VALID_PERMISSIONS: PluginPermission[] = [
  'theme:read',
  'user:profile:read',
  'storage:sandbox',
  'entities:invoke',
];

interface ConsoleEntry {
  timestamp: number;
  level: 'info' | 'warn' | 'error';
  message: string;
}

function isValidDevUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') return true;
    if (parsed.protocol === 'https:') return true;
    return false;
  } catch {
    return false;
  }
}

function parsePermissions(raw: string | null): PluginPermission[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((p): p is PluginPermission => VALID_PERMISSIONS.includes(p as PluginPermission));
}

export function PluginDevPreviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const pluginUrl = searchParams.get('url');
  const permissions = parsePermissions(searchParams.get('permissions'));

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);

  const [iframeHeight, setIframeHeight] = useState(600);
  const [iframeKey, setIframeKey] = useState(0);
  const [consoleOpen, setConsoleOpen] = useState(false);
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);

  const addConsoleEntry = useCallback((level: ConsoleEntry['level'], message: string) => {
    setConsoleEntries((prev) => [
      ...prev.slice(-199), // Keep last 200 entries
      { timestamp: Date.now(), level, message },
    ]);
  }, []);

  // Validate URL
  const urlError = !pluginUrl
    ? 'Missing "url" query parameter. Usage: ?url=http://localhost:3100&permissions=theme:read'
    : !isValidDevUrl(pluginUrl)
      ? 'URL must be localhost or HTTPS for security.'
      : null;

  // Set up bridge when iframe loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current || !pluginUrl) return;

    // Clean up previous bridge
    bridgeRef.current?.destroy();

    addConsoleEntry('info', `Bridge connecting to ${pluginUrl}`);

    bridgeRef.current = new PluginBridgeHost({
      archId: 'dev_preview',
      permissions,
      iframe: iframeRef.current,
      onResize: (height) => {
        setIframeHeight(height);
        addConsoleEntry('info', `Plugin resized to ${height}px`);
      },
      onToast: (message, variant) => {
        toast({
          description: message,
          variant: variant === 'error' ? 'destructive' : undefined,
        });
        addConsoleEntry('info', `Toast: [${variant}] ${message}`);
      },
      onNavigate: (path) => {
        navigate(path);
        addConsoleEntry('info', `Navigate: ${path}`);
      },
      getTheme: () => {
        const isDark = document.documentElement.classList.contains('dark');
        return {
          colors: {
            primary: '#6366f1',
            secondary: '#8b5cf6',
            background: isDark ? '#0f172a' : '#ffffff',
            surface: isDark ? '#1e293b' : '#f8fafc',
            text: isDark ? '#f1f5f9' : '#0f172a',
            textSecondary: isDark ? '#94a3b8' : '#64748b',
            border: isDark ? '#334155' : '#e2e8f0',
            error: '#ef4444',
            warning: '#f59e0b',
            success: '#22c55e',
          },
          spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
          fonts: {
            body: 'Inter, system-ui, sans-serif',
            heading: 'Inter, system-ui, sans-serif',
            mono: 'JetBrains Mono, monospace',
          },
          mode: isDark ? ('dark' as const) : ('light' as const),
        };
      },
      getUser: async () => ({
        userId: user?.userId ?? '',
        displayName: user?.username ?? 'User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      getAuthToken: async () => {
        const token = await authApi.getValidIdToken();
        return token ?? '';
      },
    });

    bridgeRef.current.init();
    addConsoleEntry(
      'info',
      `Bridge initialized with permissions: ${permissions.join(', ') || 'none'}`,
    );
  }, [pluginUrl, permissions, navigate, user, addConsoleEntry]);

  // Clean up bridge on unmount
  useEffect(() => {
    return () => {
      bridgeRef.current?.destroy();
    };
  }, []);

  const handleReload = useCallback(() => {
    bridgeRef.current?.destroy();
    setIframeKey((k) => k + 1);
    addConsoleEntry('info', 'Plugin reloaded');
  }, [addConsoleEntry]);

  if (urlError) {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Dev Preview Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{urlError}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              Example:{' '}
              <code className="bg-muted px-2 py-1 rounded text-xs">
                /a/arche_plugins/dev-preview?url=http://localhost:3100&permissions=theme:read,storage:sandbox
              </code>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Dev toolbar */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/50 shrink-0">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <ExternalLink className="h-4 w-4" />
          <span className="font-mono text-xs">{pluginUrl}</span>
        </div>

        <Button variant="ghost" size="sm" onClick={handleReload} title="Reload plugin">
          <RefreshCw className="h-4 w-4" />
        </Button>

        <div className="flex-1" />

        <div className="flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 text-muted-foreground" />
          {permissions.length === 0 ? (
            <span className="text-xs text-muted-foreground">No permissions</span>
          ) : (
            permissions.map((p) => (
              <Badge key={p} variant="secondary" className="text-xs">
                {p}
              </Badge>
            ))
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setConsoleOpen((v) => !v)}
          title="Toggle console"
          className={consoleOpen ? 'text-primary' : ''}
        >
          <Terminal className="h-4 w-4" />
        </Button>
      </div>

      {/* Plugin iframe */}
      <div className="flex-1 overflow-auto">
        <iframe
          key={iframeKey}
          ref={iframeRef}
          src={pluginUrl!}
          onLoad={handleIframeLoad}
          sandbox="allow-scripts allow-forms allow-same-origin"
          referrerPolicy="no-referrer"
          style={{
            width: '100%',
            height: `${iframeHeight}px`,
            border: 'none',
            display: 'block',
          }}
          title="Plugin Dev Preview"
        />
      </div>

      {/* Console panel */}
      {consoleOpen && (
        <div className="border-t bg-background shrink-0" style={{ height: '200px' }}>
          <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/30">
            <span className="text-xs font-medium text-muted-foreground">Bridge Console</span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => setConsoleEntries([])}
              >
                Clear
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setConsoleOpen(false)}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="overflow-auto h-[calc(100%-32px)] font-mono text-xs p-2 space-y-0.5">
            {consoleEntries.length === 0 ? (
              <p className="text-muted-foreground">No bridge messages yet.</p>
            ) : (
              consoleEntries.map((entry, i) => (
                <div
                  key={i}
                  className={
                    entry.level === 'error'
                      ? 'text-destructive'
                      : entry.level === 'warn'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-muted-foreground'
                  }
                >
                  <span className="opacity-50">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>{' '}
                  {entry.message}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
